import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, EMPTY, map, Observable, Subscription, withLatestFrom } from 'rxjs';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';

import { CorporateTripType, GlobalBookingTripDates, Trip, TripPassenger } from '@fcom/common/interfaces';
import { TripType } from '@fcom/core/interfaces';
import { ModalButtons } from '@fcom/ui-components/components/modal';
import { ButtonTheme } from '@fcom/ui-components/components/buttons';
import { createDeeplinkPathFromFlightSearchParams } from '@fcom/booking-widget/utils/utils';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { Location } from '@fcom/core-api/interfaces';
import { snapshot } from '@fcom/rx/utils/snapshot';
import { capitalize, LocalDate, unsubscribe } from '@fcom/core/utils';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { setPaxDetails } from '@fcom/booking/store/actions/pax-details.actions';
import { PaxDetailsFormData } from '@fcom/dapi/interfaces';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { corporateSalutationToGenderOption, splitPhoneNumber } from '@fcom/common-booking/utils/common-booking.utils';
import { PriceCalendarType } from '@fcom/common/components/price-calendar/price-calendar.component';

import { mapFinnairCabinClassToGlobalBookingTravelClass } from '../../utils/utils';

@Component({
  selector: 'fin-rebook-trip',
  templateUrl: './rebook-trip.component.html',
})
export class RebookTripComponent implements OnDestroy {
  readonly TripType = CorporateTripType;
  readonly ModalButtons = ModalButtons;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly EMPTY = EMPTY;
  readonly PriceCalendarType = PriceCalendarType;
  readonly PriceCalendarTripType = TripType;

  travelDates$: BehaviorSubject<GlobalBookingTripDates>[];
  valid$ = new BehaviorSubject<boolean>(false);
  locations: {
    [locationCode: string]: Location;
  }[] = [];
  trip: Trip;
  loading = false;
  modalOpen = false;
  subscriptions = new Subscription();

  constructor(
    private router: Router,
    private corporateService: CorporateService,
    private languageService: LanguageService,
    private store$: Store<BookingAppState>
  ) {}

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  openModal(trip: Trip): void {
    this.travelDates$ = trip.bounds.map(
      () =>
        new BehaviorSubject<GlobalBookingTripDates>({
          departureDate: undefined,
        })
    );
    this.valid$.next(false);
    this.trip = trip;
    this.modalOpen = true;
  }

  setTravelDates(travelDates: GlobalBookingTripDates, index?: number): void {
    this.travelDates$[index ?? 0].next(travelDates);
    this.valid$.next(this.hasAllDatesSet());
  }

  rebookTrip(): void {
    this.loading = true;
    this.subscriptions.add(this.setPassengersToStore().subscribe(() => this.redirectToBookingFlow()));
  }

  private redirectToBookingFlow(): void {
    this.router.navigateByUrl(
      createDeeplinkPathFromFlightSearchParams({
        travelClass: mapFinnairCabinClassToGlobalBookingTravelClass(this.trip.bookingClass),
        locale: this.languageService.langValue,
        isCorporate: true,
        paxAmount: {
          adults: this.trip.passengers.length,
          c15s: 0,
          children: 0,
          infants: 0,
        },
        tripType: this.mapTripType(this.trip.tripType),
        flights: this.trip.bounds.map((bound, index) => ({
          origin: {
            locationCode: bound.departure.locationCode,
          } as Location,
          destination: {
            locationCode: bound.arrival.locationCode,
          } as Location,
          departureDate: this.getDepartureDate(index),
        })),
      })
    );
  }

  private setPassengersToStore(): Observable<void> {
    return this.corporateService.getUsers().pipe(
      withLatestFrom(this.languageService.translate('countryCodes')),
      map(([allUsers, countryCodes]) => {
        const passengers = this.trip.passengers.map((tripPassenger, index) => {
          const corporateUser = allUsers.find(
            ({ finnairPlus }) => tripPassenger.frequentFlyerNumber && tripPassenger.frequentFlyerNumber === finnairPlus
          );
          const phone = corporateUser
            ? splitPhoneNumber(corporateUser.mobile || corporateUser.workPhone, countryCodes)
            : splitPhoneNumber(tripPassenger.phoneNumber, countryCodes);
          const birthDate = corporateUser?.birthdate?.split('-');
          return {
            firstName: corporateUser?.firstname ?? capitalize(tripPassenger.firstName),
            lastName: corporateUser?.lastname ?? capitalize(tripPassenger.lastName),
            email: corporateUser?.email ?? tripPassenger.email?.toLowerCase(),
            gender: this.getGender(corporateUser?.title, tripPassenger),
            frequentFlyerCard: {
              companyCode: corporateUser?.finnairPlus ? 'AY' : '',
              cardNumber: corporateUser?.finnairPlus,
            },
            joinFinnairPlus: false,
            phone: {
              phonePrefix: phone[0],
              phoneNumber: phone[1],
            },
            type: tripPassenger.passengerType,
            id: `ADULT${index + 1}`,
            ...(birthDate?.length === 3
              ? {
                  birthDate: {
                    dateOfBirth: birthDate[2],
                    monthOfBirth: birthDate[1],
                    yearOfBirth: birthDate[0],
                  },
                }
              : undefined),
          } as PaxDetailsFormData;
        });
        this.store$.dispatch(setPaxDetails({ passengers }));
      })
    );
  }

  private getGender(corporateUserTitle: string | undefined, tripPassenger: TripPassenger): string {
    return corporateUserTitle
      ? corporateSalutationToGenderOption(corporateUserTitle)
      : this.mapTripPassengerGender(tripPassenger);
  }

  private mapTripPassengerGender(tripPassenger: TripPassenger): string {
    switch (tripPassenger.gender) {
      case 'MALE':
        return 'mr';
      case 'FEMALE':
        return 'ms';
      default:
        return undefined;
    }
  }

  private getDepartureDate(boundIndex: number): LocalDate {
    if (this.trip.tripType === CorporateTripType.RETURN && boundIndex > 0) {
      return snapshot(this.travelDates$[0]).returnDate;
    }
    return snapshot(this.travelDates$[boundIndex]).departureDate;
  }

  private mapTripType(tripType: CorporateTripType): TripType {
    switch (tripType) {
      case CorporateTripType.RETURN:
        return TripType.RETURN;
      case CorporateTripType.MULTICITY:
        return TripType.MULTICITY;
      case CorporateTripType.ONEWAY:
      default:
        return TripType.ONEWAY;
    }
  }

  private hasAllDatesSet(): boolean {
    switch (this.trip.tripType) {
      case CorporateTripType.RETURN:
        return !!snapshot(this.travelDates$[0]).departureDate && !!snapshot(this.travelDates$[0]).returnDate;
      case CorporateTripType.MULTICITY:
        return this.travelDates$.every((date$) => !!snapshot(date$).departureDate);
      case CorporateTripType.ONEWAY:
      default:
        return !!snapshot(this.travelDates$[0]).departureDate;
    }
  }
}
